/**
 * Surface pebble scatter. Each pebble is a tiny, static bit of debris
 * at a fixed (x, z). An ant (or the player) can pick one up, carry it,
 * and drop it elsewhere. The sim only tracks logical state — the visual
 * InstancedMesh lives in PebbleMesh.
 *
 * A pebble is either:
 *   - on the ground at (x, z) with holder === -1
 *   - carried by ant `holder`; the render layer places it at the ant's mouth
 *
 * `pickupNearest(x, z, r, antId)` picks the closest available pebble within
 * `r` metres, flips it to `holder = antId`, and returns its index, or -1 if
 * none. `drop(idx, x, z)` moves it back to the ground.
 */
import type { Terrain } from '../game/Terrain';

export interface PebbleState {
  x: number;
  z: number;
  /** Ant id currently carrying this pebble, or -1 if on the ground. */
  holder: number;
}

// Collision footprint for ground pebbles. The visual is ~0.04 m tall; a
// slightly larger solid radius keeps ants from visually clipping them.
const PEBBLE_COLLIDE_R = 0.10;

export class Pebbles {
  readonly items: PebbleState[] = [];
  private readonly worldRadius: number;
  /** Dirty flag set on pickup/drop — forces the obstacle grid to be
   *  rebuilt on the next near() call. */
  private gridDirty = true;
  private cellSize = 1.0;
  private gridCols = 0;
  /** Flat 2-D grid of pebble indices; empty cells hold empty arrays. */
  private grid: number[][] = [];
  /** Reusable scratch buffer so near() doesn't allocate every frame. */
  private scratch: Array<{ x: number; z: number; radius: number }> = [];

  constructor(terrain: Terrain, worldRadius: number, count: number, seed = 1337) {
    this.worldRadius = worldRadius;
    const rng = seededRng(seed);
    for (let i = 0; i < count; i++) {
      const r = Math.sqrt(rng()) * worldRadius * 0.9;
      const a = rng() * Math.PI * 2;
      this.items.push({
        x: Math.cos(a) * r,
        z: Math.sin(a) * r,
        holder: -1,
      });
    }
    void terrain; // placement ignores height — renderer snaps to terrain each frame
  }

  /** Closest free pebble to (x, z) within `grabRadius`, or -1. */
  pickupNearest(x: number, z: number, grabRadius: number, holder: number): number {
    let bestIdx = -1;
    let bestD2 = grabRadius * grabRadius;
    for (let i = 0; i < this.items.length; i++) {
      const p = this.items[i];
      if (p.holder !== -1) continue;
      const dx = p.x - x;
      const dz = p.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
    }
    if (bestIdx === -1) return -1;
    this.items[bestIdx].holder = holder;
    this.gridDirty = true;
    return bestIdx;
  }

  /** Put pebble `idx` back on the ground at (x, z). */
  drop(idx: number, x: number, z: number): void {
    const p = this.items[idx];
    if (!p) return;
    p.x = x;
    p.z = z;
    p.holder = -1;
    this.gridDirty = true;
  }

  /** Closest free pebble to (x, z) regardless of radius. Returns -1 if all
   *  pebbles are currently held. */
  findClosestFree(x: number, z: number): number {
    let bestIdx = -1;
    let bestD2 = Infinity;
    for (let i = 0; i < this.items.length; i++) {
      const p = this.items[i];
      if (p.holder !== -1) continue;
      const dx = p.x - x;
      const dz = p.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
    }
    return bestIdx;
  }

  /** Obstacles near (x, z) for ant collision avoidance. Returns a shared
   *  scratch buffer — callers must use it before the next near() call.
   *  Held pebbles are excluded (they ride on the ant, not the ground). */
  near(x: number, z: number): Array<{ x: number; z: number; radius: number }> {
    if (this.gridDirty) this.rebuildGrid();
    const out = this.scratch;
    out.length = 0;
    if (this.gridCols === 0) return out;
    const half = this.worldRadius * 1.1;
    const cx = Math.floor((x + half) / this.cellSize);
    const cz = Math.floor((z + half) / this.cellSize);
    for (let dz = -1; dz <= 1; dz++) {
      for (let dx = -1; dx <= 1; dx++) {
        const gx = cx + dx;
        const gz = cz + dz;
        if (gx < 0 || gz < 0 || gx >= this.gridCols || gz >= this.gridCols) continue;
        const bucket = this.grid[gz * this.gridCols + gx];
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i++) {
          const p = this.items[bucket[i]];
          out.push({ x: p.x, z: p.z, radius: PEBBLE_COLLIDE_R });
        }
      }
    }
    return out;
  }

  private rebuildGrid(): void {
    const half = this.worldRadius * 1.1;
    this.cellSize = 1.0;
    this.gridCols = Math.max(1, Math.ceil((half * 2) / this.cellSize));
    this.grid = new Array(this.gridCols * this.gridCols);
    for (let i = 0; i < this.items.length; i++) {
      const p = this.items[i];
      if (p.holder !== -1) continue;
      const gx = Math.floor((p.x + half) / this.cellSize);
      const gz = Math.floor((p.z + half) / this.cellSize);
      if (gx < 0 || gz < 0 || gx >= this.gridCols || gz >= this.gridCols) continue;
      const key = gz * this.gridCols + gx;
      const bucket = this.grid[key] ?? (this.grid[key] = []);
      bucket.push(i);
    }
    this.gridDirty = false;
  }
}

function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
